import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard,
  Package,
  Users,
  Building2,
  Tag,
  Star,
  LogOut,
  Menu,
  X,
  Bike,
  ChevronRight,
  Bell,
  Briefcase,
  DollarSign,
  Settings,
  TrendingUp,
} from "lucide-react";

const NAV_CONFIG = [
  {
    to: "/",
    icon: LayoutDashboard,
    label: "Dashboard",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/orders",
    icon: Package,
    label: "Orders",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/riders",
    icon: Bike,
    label: "Riders",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/users",
    icon: Users,
    label: "Users",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/branches",
    icon: Building2,
    label: "Branches",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/financials",
    icon: DollarSign,
    label: "Financials",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/promotions",
    icon: Tag,
    label: "Promotions",
    roles: ["super_admin", "admin"],
  },
  {
    to: "/ratings",
    icon: Star,
    label: "Ratings",
    roles: ["super_admin", "admin", "branch_manager"],
  },
  {
    to: "/b2b",
    icon: Briefcase,
    label: "B2B Clients",
    roles: ["super_admin", "admin"],
  },
  {
    to: "/investments",
    icon: TrendingUp,
    label: "Investments",
    roles: ["super_admin", "admin"],
  },
  {
    to: "/settings",
    icon: Settings,
    label: "Settings",
    roles: ["super_admin", "admin", "branch_manager"],
  },
];

const ROLE_STYLE = {
  super_admin: { label: "Super Admin", color: "#ef4444" },
  admin: { label: "Admin", color: "#3b82f6" },
  branch_manager: { label: "Branch Manager", color: "#a855f7" },
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const role = user?.role || "";
  const roleStyle = ROLE_STYLE[role] || { label: role, color: "var(--accent)" };
  const visibleNav = NAV_CONFIG.filter((n) => n.roles.includes(role));

  return (
    <div className="layout">
      <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <div className="logo">
            {sidebarOpen && (
              <img
                src="/logo.png"
                alt="Kwikie Express Logo"
                className="sidebar-logo"
              />
            )}
          </div>

          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen((o) => !o)}
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {sidebarOpen && (
          <div style={{ padding: "8px 16px 0" }}>
            <div
              style={{
                background: `${roleStyle.color}18`,
                border: `1px solid ${roleStyle.color}40`,
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 700,
                color: roleStyle.color,
                textAlign: "center",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {roleStyle.label}
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {visibleNav.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              title={!sidebarOpen ? label : ""}
            >
              <Icon size={18} />
              {sidebarOpen && <span>{label}</span>}
              {sidebarOpen && <ChevronRight size={14} className="nav-arrow" />}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <div className="user-info">
              <div className="user-avatar">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.name}</div>
                <div className="user-role" style={{ color: roleStyle.color }}>
                  {roleStyle.label}
                </div>
              </div>
            </div>
          )}
          <button
            className="logout-btn"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            title="Logout"
          >
            <LogOut size={16} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="main-wrap">
        <header className="topbar">
          <div className="topbar-left">
            <div className="breadcrumb">Admin Panel</div>
          </div>
          <div className="topbar-right">
            <div className="topbar-badge">
              <Bell size={18} />
              <span className="badge-dot" />
            </div>
            <div className="topbar-user">
              <div className="avatar-sm">{user?.name?.[0]?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </div>
          </div>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}